import { useMemo, useState } from 'react';
import { TableFooter, TableRow, TableCell, Box, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight, AttachMoney } from '@mui/icons-material';
import type { Table as TanStackTable } from '@tanstack/react-table';
import type { ColumnMetadata } from '../../../types';
import { isSummaryColumn } from '../utils/columnUtils';
import {
  FULL_FOOTER_TYPES,
  SUMMARY_TYPE_CUSTOM_LEFT_FOOTER,
  SUMMARY_TYPE_CUSTOM_RIGHT_FOOTER,
} from '../utils/summaryConstants';
import {
  getFieldValue,
  formatHoursDecimal,
  formatTimeStringToText,
  getCurrencySymbol,
  parseAmountStats,
  calculateClientFallback,
  type AmountStat,
} from '../utils/summaryCalculators';

type DataGridFooterProps<T> = {
  table: TanStackTable<T>;
  columnMetadataMap: Map<string, ColumnMetadata>;
  serverSummary?: string[] | null;
  hiddenColumnIds?: Set<string>;
};

// ---------------------------------------------------------------------------
// custom-left-footer — "Tracked / Manual / Overtime" widget.
// Not a per-row aggregation: reads pre-aggregated totals off the first
// loaded row's Fields, exactly like legacy onCellPrepared (rowType ===
// "totalFooter", reading dataSourceItems[0]).
// ---------------------------------------------------------------------------

const TrackedFooterWidget = ({ firstRow }: { firstRow: unknown }) => {
  if (!firstRow) return null;

  const trackedWithTracker = Number(getFieldValue(firstRow, 'TotalTrackedWithTracker')) || 0;
  const trackedManually = Number(getFieldValue(firstRow, 'TotalTrackedManually')) || 0;
  const totalOvertime = getFieldValue(firstRow, 'TotalOvertime') ?? 0;

  const items = [
    {
      color: '#28a745',
      label: 'Tracked',
      value: formatHoursDecimal(trackedWithTracker),
    },
    {
      color: '#007bff',
      label: 'Manual',
      value: formatHoursDecimal(trackedManually),
    },
    {
      color: '#fd7e14',
      label: 'Overtime',
      value: formatHoursDecimal(totalOvertime),
    },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', px: 1.5, py: 0.5 }}>
      {items.map((item) => (
        <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: item.color }} />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box component="span" sx={{ fontWeight: 600, fontSize: 'inherit' }}>
              {item.label}
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: 'inherit',
                color: (theme) =>
                  theme.palette.mode === 'dark' ? 'text.primary' : 'text.secondary',
              }}
            >
              {formatTimeStringToText(item.value)}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// custom-right-footer — currency amount slider. Reads `AmountStats` JSON
// (array of { Currency, CurrencySymbol?, Amount, TotalTime, RateWasChanged,
// RatePerHour }) off the first loaded row, matching legacy's slider markup.
// ---------------------------------------------------------------------------

const AmountFooterWidget = ({ firstRow }: { firstRow: unknown }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!firstRow) return null;

  const amountStats: AmountStat[] = parseAmountStats(getFieldValue(firstRow, 'AmountStats'));
  const showButtons = amountStats.length > 1;
  const item = amountStats[currentIndex];

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
      {showButtons && (
        <IconButton
          size="small"
          aria-label="Previous currency"
          disabled={currentIndex === 0}
          onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
        >
          <ChevronLeft fontSize="small" />
        </IconButton>
      )}
      <Box sx={{ px: 1.5, textAlign: 'left', minWidth: 220 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            fontSize: 'inherit',
            color: 'text.primary',
          }}
        >
          <AttachMoney fontSize="small" sx={{ mr: 0.5 }} />
          <Box component="span" sx={{ fontWeight: 600 }}>
            Total amount
          </Box>
        </Box>
        <Box sx={{ fontWeight: 'bold', fontSize: 'inherit', mt: 0.25 }}>
          {item ? `${getCurrencySymbol(item)}${item.Amount.toFixed(2)}` : '$0.00'}
        </Box>
        <Box
          sx={{
            fontSize: 'inherit',
            color: (theme) => (theme.palette.mode === 'dark' ? 'text.primary' : 'text.secondary'),
            mt: 0.25,
          }}
        >
          {item
            ? `${formatHoursDecimal(item.TotalTime ?? 0)} hrs ${
                !item.RateWasChanged
                  ? `${getCurrencySymbol(item)}${(item.RatePerHour ?? 0).toFixed(2)} per hr`
                  : 'with several rates'
              }`
            : '0:00 hrs $0.00 per hr'}
        </Box>
      </Box>
      {showButtons && (
        <IconButton
          size="small"
          aria-label="Next currency"
          disabled={currentIndex === amountStats.length - 1}
          onClick={() => setCurrentIndex((i) => Math.min(amountStats.length - 1, i + 1))}
        >
          <ChevronRight fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const DataGridFooter = <T extends Record<string, unknown>>({
  table,
  columnMetadataMap,
  serverSummary,
  hiddenColumnIds,
}: DataGridFooterProps<T>) => {
  const summaryColumns = useMemo(
    () => Array.from(columnMetadataMap.entries()).filter(([, meta]) => isSummaryColumn(meta)),
    [columnMetadataMap]
  );

  const rows = table.getPrePaginationRowModel().rows;
  const firstRowOriginal = rows[0]?.original;

  const sums = useMemo(() => {
    const totals = new Map<string, string>();
    if (summaryColumns.length === 0) return totals;

    const hasServerSummary = Array.isArray(serverSummary) && serverSummary.length > 0;

    for (let i = 0; i < summaryColumns.length; i++) {
      const [colId, meta] = summaryColumns[i];
      const summaryType = (meta.totalSummaries ?? '').toLowerCase();

      if (FULL_FOOTER_TYPES.has(summaryType)) continue;

      if (hasServerSummary) {
        const raw = serverSummary[i];
        if (raw != null) {
          totals.set(colId, raw);
          continue;
        }
      }

      totals.set(colId, calculateClientFallback(rows, colId, summaryType));
    }

    return totals;
  }, [rows, summaryColumns, serverSummary]);

  if (summaryColumns.length === 0) return null;

  const summaryMetaByColId = new Map(summaryColumns);

  return (
    <TableFooter
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 9,
        backgroundColor: 'background.paper',
      }}
    >
      <TableRow>
        {table
          .getVisibleLeafColumns()
          .filter((col) => !hiddenColumnIds?.has(col.id))
          .map((col) => {
            const meta = summaryMetaByColId.get(col.id);
            const summaryType = (meta?.totalSummaries ?? '').toLowerCase();

            if (summaryType === SUMMARY_TYPE_CUSTOM_LEFT_FOOTER) {
              return (
                <TableCell
                  key={col.id}
                  sx={{ borderTop: '2px solid', borderTopColor: 'divider', p: 0 }}
                >
                  <TrackedFooterWidget firstRow={firstRowOriginal} />
                </TableCell>
              );
            }

            if (summaryType === SUMMARY_TYPE_CUSTOM_RIGHT_FOOTER) {
              return (
                <TableCell
                  key={col.id}
                  sx={{ borderTop: '2px solid', borderTopColor: 'divider', p: 0 }}
                >
                  <AmountFooterWidget firstRow={firstRowOriginal} />
                </TableCell>
              );
            }

            const text = sums.get(col.id);
            return (
              <TableCell
                key={col.id}
                sx={{
                  fontSize: 'inherit',
                  fontWeight: 600,
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? 'text.primary' : 'text.secondary',
                  borderTop: '2px solid',
                  borderTopColor: 'divider',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {text ?? ''}
              </TableCell>
            );
          })}
      </TableRow>
    </TableFooter>
  );
};
